import React, { useState, useEffect } from 'react';
import { Link, NavLink } from 'react-router-dom';
import { motion } from 'framer-motion';
import { navLinks, companyInfo } from '../data';
import logo from "../assets/images/logo.png";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Safe fallback for navLinks
  const links = navLinks || [];
  const info = companyInfo || {};

  return (
    <nav 
      className={`fixed w-full z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white shadow-lg py-2' : 'bg-transparent py-4'
      }`}
    >
      <div className="container-custom flex items-center justify-between">
        {/* Logo and Company Name */}
        <Link to="/" className="flex items-center space-x-3 group">
          <img 
            src={logo} 
            alt={info.name || 'Company Logo'}
            className={`transition-all duration-300 ${
              isScrolled ? 'h-12' : 'h-16'
            }`}
          />
          <div className="flex flex-col">
            <span className={`font-oswald font-bold text-lg leading-tight transition-colors duration-300 ${
              isScrolled ? 'text-secondary' : 'text-secondary'
            }`}>
              {info.shortName || 'Al Abeer Diamond Company LLC.'}
            </span>
            <span className={`text-xs font-roboto tracking-wider transition-colors duration-300 ${
              isScrolled ? 'text-gray-500' : 'text-gray-500'
            }`}>
              {info.tagline || 'Building Excellence, Ensuring Safety'}
            </span>
          </div>
        </Link>

        {/* Desktop Menu */}
        <div className="hidden lg:flex items-center space-x-8">
          {links.map((link) => (
            <NavLink
              key={link.id}
              to={link.href}
              className={({ isActive }) =>
                `font-medium transition-colors duration-300 ${
                  isActive 
                    ? 'text-primary' 
                    : isScrolled 
                    ? 'text-secondary hover:text-primary' 
                    : 'text-gray-800 hover:text-primary' // Changed to gray/black in non-scrolling view
                }`
              }
            >
              {link.title}
            </NavLink>
          ))}
        </div>

        {/* Mobile Menu Button - Always visible on mobile */}
        <button
          className="lg:hidden text-2xl"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle menu"
        >
          {isOpen ? (
            <i className={`uil uil-times ${isScrolled ? 'text-secondary' : 'text-white'}`}></i>
          ) : (
            <i className={`uil uil-bars ${isScrolled ? 'text-secondary' : 'text-white'}`}></i>
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
        className={`lg:hidden overflow-hidden ${
          isScrolled ? 'bg-white' : 'bg-secondary'
        }`}
      >
        <div className="container-custom py-4 space-y-3">
          {links.map((link) => (
            <NavLink
              key={link.id}
              to={link.href}
              className={({ isActive }) =>
                `block py-2 font-medium transition-colors ${
                  isActive 
                    ? 'text-primary' 
                    : isScrolled 
                    ? 'text-secondary hover:text-primary' 
                    : 'text-gray-200 hover:text-primary' // Light gray for mobile dark background
                }`
              }
              onClick={() => setIsOpen(false)}
            >
              {link.title}
            </NavLink>
          ))}
        </div>
      </motion.div>
    </nav>
  );
};

export default Navbar;